import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { Tag, Spin } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import trans from "../../../utils";
import BreadCrumb from "../../Header/SecondHeader";
import PopupBodyItem from "../../Helpers/Popup/PopupParts/PopupBodyItem";
import { formatDateString } from "../../../JsHelpers/DateFormat";
import { useGetServiceRequestDetailsQuery } from "../../../Redux/service/Dispatcher/Dispatcher";
import ActivityTimeline from "./ActivityTimeline";

const DISPATCH_STATUS_COLORS = {
  open: "default",
  assigned: "purple",
  pending: "orange",
  inProgress: "blue",
  completed: "green",
  closed: "cyan",
  cancelled: "red",
};

export default function DispatcherDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: detailsResp, isFetching: loadingDetails } = useGetServiceRequestDetailsQuery(id);

  const data = detailsResp?.data;

  return (
    <>
      <Helmet>
        <title>Service Request Details</title>
      </Helmet>
      <BreadCrumb />
      <div className="p-16">
        <button
          type="button"
          className="btn btn-link p-0 mb-3"
          onClick={() => navigate(-1)}
        >
          <ArrowLeftOutlined /> {trans("Back")}
        </button>

        {loadingDetails ? (
          <Spin />
        ) : !data ? (
          <p>{trans("Service request not found")}</p>
        ) : (
          <>
            <div className="d-flex align-items-center gap-3 mb-3">
              <p className="fs-16 fw-500 brand-700 text-uppercase mb-0">
                {trans("Request")} {data.requestNumber}
              </p>
              <Tag color={DISPATCH_STATUS_COLORS[data.status] || "default"} className="text-capitalize">
                {trans(data.status)}
              </Tag>
            </div>

            <div className="row">
              {data.customer && (
                <div className="col-lg-6">
                  <p className="fw-500 mb-2">{trans("Customer Information")}</p>
                  <div className="display-border">
                    <PopupBodyItem title={trans("Customer Name")} item={data.customer.name} />
                    <PopupBodyItem title={trans("Mobile Number")} item={data.customer.phoneNumber} />
                    <PopupBodyItem
                      title={trans("Additional Phones")}
                      item={Array.isArray(data.customer.secondaryPhones) ? data.customer.secondaryPhones.join(", ") : "-"}
                    />
                    <PopupBodyItem title={trans("Home Telephone")} item={data.customer.landline} />
                  </div>
                </div>
              )}

              <div className="col-lg-6">
                <p className="fw-500 mb-2">{trans("Address")}</p>
                <div className="display-border">
                  <PopupBodyItem title={trans("Governorate")} item={trans(data.address.governorate)} />
                  <PopupBodyItem title={trans("Markaz/Qism")} item={trans(data.address.markaz)} />
                  <PopupBodyItem title={trans("Village")} item={data.address.village} />
                  <PopupBodyItem title={trans("Full Address")} item={data.address.address} />
                </div>
              </div>

              <div className="col-lg-6 mt-3">
                <p className="fw-500 mb-2">{trans("Product Information")}</p>
                <div className="display-border">
                  <PopupBodyItem title={trans("Product")} item={data.product?.name} />
                  <PopupBodyItem title={trans("Model")} item={data.model?.name} />
                  <PopupBodyItem
                    title={trans("Call Before Performing")}
                    item={
                      <Tag color={data.callBeforePerforming ? "green" : "default"}>
                        {trans(data.callBeforePerforming ? "Yes" : "No")}
                      </Tag>
                    }
                  />
                  <PopupBodyItem
                    title={trans("Defects / Issues")}
                    item={
                      Array.isArray(data.defects) && data.defects.length
                        ? data.defects.map((d) => d?.name || d?.id || d).filter(Boolean).join(", ")
                        : "-"
                    }
                  />
                  <PopupBodyItem
                    title={trans("Spare Parts")}
                    item={
                      Array.isArray(data.spareParts) && data.spareParts.length
                        ? data.spareParts.map((p) => p?.name || p?.id || p).filter(Boolean).join(", ")
                        : "-"
                    }
                  />
                </div>
              </div>

              <div className="col-lg-6 mt-3">
                <p className="fw-500 mb-2">{trans("Service Request Information")}</p>
                <div className="display-border">
                  <PopupBodyItem title={trans("Complaint Description")} item={data.complaint} />
                  <PopupBodyItem
                    title={trans("Preferred Visit Time")}
                    item={data.preferredVisitTime ? formatDateString(data.preferredVisitTime) : "-"}
                  />
                  <PopupBodyItem title={trans("Request Date")} item={formatDateString(data.requestedDate)} />
                  <PopupBodyItem title={trans("Created By")} item={data.createdBy?.name} />
                </div>
              </div>

              <div className="col-lg-6 mt-3">
                <p className="fw-500 mb-2">{trans("Assignment Information")}</p>
                <div className="display-border">
                  <PopupBodyItem
                    title={trans("Assigned Technician")}
                    item={data.assignment.technician?.name || trans("Unassigned")}
                  />
                  <PopupBodyItem
                    title={trans("Assignment Date")}
                    item={data.assignment.assignmentDate ? formatDateString(data.assignment.assignmentDate) : "-"}
                  />
                  <PopupBodyItem
                    title={trans("Current Status")}
                    item={<Tag color={DISPATCH_STATUS_COLORS[data.assignment.currentStatus] || "default"}>{trans(data.assignment.currentStatus)}</Tag>}
                  />
                </div>
              </div>
            </div>

            <p className="fs-16 fw-500 brand-700 text-uppercase mt-4">{trans("Activity History")}</p>
            <ActivityTimeline requestId={id} />
          </>
        )}
      </div>
    </>
  );
}
